import React, {useCallback, useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {toastDefaultConfig} from '@/shared/config/toastConfig'
import {getLogger} from '@/shared/utils/logUtils'
import {CustomModal} from '@/app/modules/widgets/components/CustomModal'
import {listConnections, revokeConnection, SCOPE_LABELS, type Connection} from './api'

const formatDate = (epochSeconds?: number | null): string =>
  epochSeconds
    ? new Date(epochSeconds * 1000).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '—'

/**
 * Apps the user connected over OAuth — Claude, ChatGPT, Cursor and friends — identified by the host
 * that publishes their application details. A self-declared name is never shown as the identity: for
 * a dynamically-registered client we have nothing but what it told us, and this makes that visible
 * rather than dressing it up as verified.
 */
const clientIdentity = (connection: Connection): {label: string; verified: boolean} =>
  connection.clientSource === 'cimd'
    ? {label: connection.clientHost, verified: true}
    : {label: connection.clientHost, verified: false}

/**
 * A short, human description of which session a row is.
 *
 * Deliberately one row per authorization rather than one per app: a native client signs in on each
 * machine separately, so five computers are five sessions and collapsing them would hide exactly what
 * someone checks this page for. The user agent is what distinguishes them.
 */
const sessionDescription = (connection: Connection): string | null => {
  const ua = connection.lastUserAgent
  if (!ua) return null
  // Client user agents are short and meaningful ("claude-code/1.4.2 (macOS)"); browser ones are long
  // and are not worth rendering in a table cell.
  const compact = ua.length > 48 ? `${ua.slice(0, 48)}…` : ua
  return connection.lastClientIp ? `${compact} · ${connection.lastClientIp}` : compact
}

const scopeSummary = (scopes: string[]): string =>
  scopes.map((s) => SCOPE_LABELS[s]?.title ?? s).join(' · ')

/**
 * "Connected apps" settings card.
 *
 * Self-gates like the API-keys card next to it: an account that has never connected an app sees
 * nothing, because a permanently empty section is just noise. It appears the moment there is
 * something to manage.
 *
 * Revoking here is immediate and total — the app's refresh chain is broken and every access token it
 * holds is deleted, so its very next call fails. That is only true because the connector token is
 * opaque and looked up on each request; it is what lets the token itself be long-lived.
 */
export const ConnectedAppsSection: React.FC = () => {
  const [connections, setConnections] = useState<Connection[] | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [revoking, setRevoking] = useState<string | null>(null)
  const [confirm, setConfirm] = useState<Connection | null>(null)

  const fetchConnections = useCallback(async () => {
    setIsLoading(true)
    try {
      setConnections(await listConnections())
    } catch (err) {
      // A facade that is unreachable or not yet deployed must not break Settings — the section simply
      // does not appear.
      getLogger().warn('Could not load connected apps', err)
      setConnections(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchConnections()
  }, [fetchConnections])

  const handleRevoke = async () => {
    if (!confirm) return
    const target = confirm
    setRevoking(target.connectionId)
    try {
      await revokeConnection(target.connectionId)
      setConnections((prev) => (prev ?? []).filter((c) => c.connectionId !== target.connectionId))
      setConfirm(null)
      toast.success(`Disconnected ${target.clientHost}`, toastDefaultConfig)
    } catch (err: any) {
      toast.error(err?.message || 'Could not disconnect that app', toastDefaultConfig)
      getLogger().error('Revoke connection failed:', err)
    } finally {
      setRevoking(null)
    }
  }

  // Nothing connected and nothing loading: stay out of the way.
  if (isLoading || !connections || connections.length === 0) return null

  return (
    <div className='col-12 col-lg-6'>
      <div className='ob-page__section d-flex flex-column flex-grow-1'>
        <h3 className='ob-page__section-title'>
          <i className='fas fa-plug me-2' />
          Connected apps
        </h3>
        <p className='ob-text--muted ob-text--small ob-spacing--mb-4'>
          Assistants and tools you have allowed to use OmicsCloud on your behalf. Desktop apps sign in
          per computer, so the same app can appear more than once — one row per sign-in. Disconnecting
          takes effect immediately, and only for that row.
        </p>

        <div className='card'>
          <div className='card-body'>
            <div className='table-responsive ob-settings-table'>
              <table className='table table-hover align-middle mb-0' style={{fontSize: '0.9rem'}}>
                <thead style={{backgroundColor: '#f8f9fa'}}>
                  <tr>
                    <th style={{padding: '0.5rem'}}>App</th>
                    <th style={{padding: '0.5rem'}}>Access</th>
                    <th style={{padding: '0.5rem'}}>Billed to</th>
                    <th style={{padding: '0.5rem'}}>Connected</th>
                    <th style={{padding: '0.5rem'}}>Last used</th>
                    <th style={{padding: '0.5rem'}} />
                  </tr>
                </thead>
                <tbody>
                  {connections.map((connection) => {
                    const identity = clientIdentity(connection)
                    return (
                      <tr key={connection.connectionId}>
                        <td style={{padding: '0.5rem'}}>
                          <span className='fw-semibold'>{identity.label}</span>
                          {!identity.verified && (
                            <span
                              className='badge badge-light-warning ms-2'
                              title='This app registered itself and chose its own name. It has not been verified.'
                            >
                              self-declared
                            </span>
                          )}
                          {sessionDescription(connection) && (
                            <div className='text-muted fs-8'>{sessionDescription(connection)}</div>
                          )}
                        </td>
                        <td style={{padding: '0.5rem'}} className='text-muted'>
                          {scopeSummary(connection.scopes)}
                        </td>
                        <td style={{padding: '0.5rem'}} className='text-muted'>
                          {connection.subscriptionId || '—'}
                        </td>
                        <td style={{padding: '0.5rem'}} className='text-muted'>
                          {formatDate(connection.createdAt)}
                        </td>
                        <td style={{padding: '0.5rem'}} className='text-muted'>
                          {formatDate(connection.lastUsedAt)}
                        </td>
                        <td style={{padding: '0.5rem'}} className='text-end'>
                          <button
                            type='button'
                            className='btn btn-sm btn-light-danger'
                            onClick={() => setConfirm(connection)}
                            disabled={revoking === connection.connectionId}
                          >
                            {revoking === connection.connectionId ? 'Disconnecting…' : 'Disconnect'}
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {confirm && (
        <CustomModal
          title='Disconnect this app?'
          closeModal={() => {
            if (!revoking) setConfirm(null)
          }}
          buttons={[
            {
              text: 'Cancel',
              className: 'btn btn-light',
              onClick: () => setConfirm(null),
              disabled: !!revoking,
            },
            {
              text: revoking ? 'Disconnecting…' : 'Disconnect',
              className: 'btn btn-danger',
              onClick: () => void handleRevoke(),
              disabled: !!revoking,
            },
          ]}
        >
          <div>
            <p className='mb-2'>
              <strong>{confirm.clientHost}</strong> will lose access straight away. Anything it is
              running now may fail.
            </p>
            <p className='mb-0 text-muted' style={{fontSize: '0.9em'}}>
              Results already produced stay in your cloud storage. You can connect the app again at any
              time.
            </p>
          </div>
        </CustomModal>
      )}
    </div>
  )
}
